import { useEffect, useRef } from 'react';

const DEFAULT_ADMAX_SCRIPT_SRC = 'https://adm.shinobi.jp/s/1eead92a44a0cd5197bf1acbf0fc0afe';
const ADMAX_ACTION_RUNTIME_SRC = 'https://adm.shinobi.jp/st/s.js';
const ADMAX_STICKY_WRAPPER_SELECTOR = '[data-admax-sticky-wrapper]';
const FLOATING_AD_EDGE_PX = 14;

function cleanupStickyArtifacts() {
  if (typeof document === 'undefined') return;

  document.querySelectorAll('style').forEach((styleEl) => {
    const css = styleEl.textContent || '';
    if (css.includes('admax-official-overlap-close-')) {
      styleEl.remove();
    }
  });

  const wrapper = document.querySelector(ADMAX_STICKY_WRAPPER_SELECTOR);
  if (!wrapper || !wrapper.parentNode) return;

  const parent = wrapper.parentNode;
  const main = wrapper.firstElementChild;
  Array.from(wrapper.children).forEach((child, index) => {
    if (index > 0) child.remove();
  });

  if (main) {
    while (main.firstChild) {
      parent.insertBefore(main.firstChild, wrapper);
    }
  }

  parent.removeChild(wrapper);
}

function resetStickyGlobals() {
  if (typeof window === 'undefined') return;

  window.ninja_sticky_bottom_ads = undefined;
  window.ninja_sticky_right_ads = undefined;
  window.ninja_sticky_sidebar_ads = undefined;
  window.admaxaction = undefined;
  window.__admax_tag__ = undefined;
  window.__admax_render__ = undefined;
}

function forceStickyBottomRightPlacement() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  const wrapper = document.querySelector(ADMAX_STICKY_WRAPPER_SELECTOR);
  if (!wrapper) return false;

  const main = wrapper.firstElementChild;
  if (main && main.style) {
    main.style.removeProperty('margin-bottom');
  }

  const stickyContainers = Array.from(wrapper.children).slice(1);
  let applied = false;

  stickyContainers.forEach((container) => {
    const computed = window.getComputedStyle(container);
    if (computed.position !== 'fixed') return;

    container.style.left = 'auto';
    container.style.right = `${FLOATING_AD_EDGE_PX}px`;
    container.style.top = 'auto';
    container.style.bottom = `${FLOATING_AD_EDGE_PX}px`;
    container.style.transform = 'none';
    container.style.marginLeft = '0';
    container.style.zIndex = '2147483647';
    applied = true;
  });

  return applied;
}

export default function AdMaxUnit({
  scriptSrc = DEFAULT_ADMAX_SCRIPT_SRC,
  title = 'AdMax Advertisement',
  useIframe = true,
}) {
  const slotRef = useRef(null);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot || !scriptSrc) return undefined;

    const isLocalHost = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    );
    const allowLocalRealAds = process.env.REACT_APP_AD_ALLOW_LOCAL_REAL === 'true';
    const isLocalPreview = process.env.NODE_ENV !== 'production' && isLocalHost && !allowLocalRealAds;
    const showNoFillPlaceholder =
      process.env.REACT_APP_AD_SHOW_NOFILL_PLACEHOLDER !== 'false' &&
      process.env.NODE_ENV !== 'production';

    slot.innerHTML = '';
    if (isLocalPreview) {
      const placeholder = document.createElement('div');
      placeholder.setAttribute('aria-label', 'Local ad preview');
      placeholder.style.width = '100%';
      placeholder.style.maxWidth = '100%';
      placeholder.style.height = '90px';
      placeholder.style.margin = '0 auto';
      placeholder.style.display = 'flex';
      placeholder.style.alignItems = 'center';
      placeholder.style.justifyContent = 'center';
      placeholder.style.border = '1px dashed #94a3b8';
      placeholder.style.borderRadius = '8px';
      placeholder.style.background = 'transparent';
      placeholder.style.color = '#1d4ed8';
      placeholder.style.fontSize = '14px';
      placeholder.style.fontWeight = '600';
      placeholder.textContent = 'Ad slot preview (localhost)';
      slot.appendChild(placeholder);
      return () => {
        slot.innerHTML = '';
      };
    }

    if (!useIframe) {
      cleanupStickyArtifacts();
      resetStickyGlobals();

      let destroyed = false;
      let stickyObserver = null;
      const syncStickyPlacement = () => {
        if (destroyed) return;
        const placed = forceStickyBottomRightPlacement();
        if (placed && stickyObserver) {
          stickyObserver.disconnect();
          stickyObserver = null;
        }
      };
      const watchStickyPlacement = () => {
        if (destroyed || typeof MutationObserver === 'undefined' || stickyObserver) return;

        stickyObserver = new MutationObserver(() => {
          syncStickyPlacement();
        });
        stickyObserver.observe(document.body, { childList: true, subtree: true });
      };

      const loadActionRuntimeIfNeeded = () => {
        if (destroyed) return;
        if (typeof window === 'undefined') return;
        if (window.__admax_render__) return;

        const hasPendingAdmaxTag = Boolean(
          window.admaxaction || window.admaxbanner || window.admaxoverlay
        );
        if (!hasPendingAdmaxTag) return;

        const runtimeScript = document.createElement('script');
        runtimeScript.src = ADMAX_ACTION_RUNTIME_SRC;
        runtimeScript.async = true;
        slot.appendChild(runtimeScript);
      };

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = false;
      script.onload = () => {
        window.setTimeout(loadActionRuntimeIfNeeded, 0);
        window.setTimeout(syncStickyPlacement, 50);
        window.setTimeout(syncStickyPlacement, 250);
        watchStickyPlacement();
      };
      slot.appendChild(script);
      return () => {
        destroyed = true;
        if (stickyObserver) stickyObserver.disconnect();
        slot.innerHTML = '';
        cleanupStickyArtifacts();
        resetStickyGlobals();
      };
    }

    const iframe = document.createElement('iframe');
    iframe.title = title;
    iframe.setAttribute('scrolling', 'no');
    iframe.style.width = '100%';
    iframe.style.maxWidth = '100%';
    iframe.style.height = '120px';
    iframe.style.minHeight = '90px';
    iframe.style.border = '0';
    iframe.style.display = 'block';
    iframe.style.background = 'transparent';
    slot.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    let sizePollTimer = null;
    let stopPollTimer = null;
    let resizeObserver = null;
    let noFillTimer = null;

    if (doc) {
      const syncSize = () => {
        const contentHeight = Math.max(
          doc.documentElement?.scrollHeight || 0,
          doc.body?.scrollHeight || 0
        );

        if (contentHeight > 0) {
          iframe.style.height = `${Math.max(90, contentHeight)}px`;
        }
      };

      doc.open();
      const scriptTag = [`<script src="${scriptSrc}"></scr`, 'ipt>'].join('');
      doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        background: transparent;
      }
      body {
        text-align: center;
      }
    </style>
  </head>
  <body>
    ${scriptTag}
  </body>
</html>`);
      doc.close();

      sizePollTimer = window.setInterval(syncSize, 250);
      stopPollTimer = window.setTimeout(() => {
        if (sizePollTimer !== null) {
          window.clearInterval(sizePollTimer);
          sizePollTimer = null;
        }
      }, 8000);

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncSize);
        resizeObserver.observe(slot);
      }

      window.setTimeout(syncSize, 50);
      window.setTimeout(syncSize, 500);
      window.setTimeout(syncSize, 1500);

      if (showNoFillPlaceholder) {
        noFillTimer = window.setTimeout(() => {
          const iframeDoc = iframe.contentWindow?.document;
          if (!iframeDoc) return;

          const hasContent = Boolean(
            iframeDoc.body &&
            iframeDoc.body.innerHTML &&
            iframeDoc.body.innerHTML.trim().length > 0 &&
            !/^\s*<script[\s\S]*<\/script>\s*$/.test(iframeDoc.body.innerHTML)
          );

          if (!hasContent) {
            slot.innerHTML = '';
            const placeholder = document.createElement('div');
            placeholder.setAttribute('aria-label', 'Ad no fill');
            placeholder.style.width = '100%';
            placeholder.style.maxWidth = '728px';
            placeholder.style.height = '90px';
            placeholder.style.minHeight = '90px';
            placeholder.style.display = 'flex';
            placeholder.style.alignItems = 'center';
            placeholder.style.justifyContent = 'center';
            placeholder.style.border = '1px dashed #cbd5e1';
            placeholder.style.borderRadius = '8px';
            placeholder.style.color = '#64748b';
            placeholder.style.fontSize = '12px';
            placeholder.textContent = 'Ad slot ready (no fill / blocked)';
            slot.appendChild(placeholder);
          }
        }, 1800);
      }
    }

    return () => {
      if (sizePollTimer !== null) window.clearInterval(sizePollTimer);
      if (stopPollTimer !== null) window.clearTimeout(stopPollTimer);
      if (noFillTimer !== null) window.clearTimeout(noFillTimer);
      if (resizeObserver) resizeObserver.disconnect();
      slot.innerHTML = '';
    };
  }, [scriptSrc, title, useIframe]);

  return <div ref={slotRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />;
}
